'use client'

import { Card, Cell, Dialog, Image, Spacer, Text } from '@vinted/web-ui'
import { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import { navigateToPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useAsset } from '@marketplace-web/shared/assets'
import { ItemBox } from '@marketplace-web/item-box/item-box-feature'
import { ItemBoxDto } from '@marketplace-web/item-box/product-item-data'
import {
  clickEvent,
  itemUploadViewScreenEvent,
  getUploadAnotherItemTip,
} from '@marketplace-web/item-upload/after-upload-actions-data'
import {
  transformCurrencyAmountDto,
  formatCurrencyAmount,
} from '@marketplace-web/currency/currency-data'
import { findThumbnail } from '@marketplace-web/shared/utils'

import { ITEM_UPLOAD_URL } from '../../../constants/routes'

type PartialItemBoxProps = Pick<React.ComponentProps<typeof ItemBox>, 'price' | 'image'> & {
  brand: string | null
  size: string | null
  itemBox?: ItemBoxDto | null
}

enum ItemThumbnailSize {
  Medium = 'thumb150x210',
  Large = 'thumb310x430',
}

const getItemThumbnail = (
  photos: Array<{
    is_main: boolean
    thumbnails: Array<{ type: ItemThumbnailSize | string; url: string }>
  }>,
  size = ItemThumbnailSize.Medium,
) => {
  const photo = photos?.find(({ is_main }) => is_main)

  return findThumbnail(photo, size)
}

export type ListPromotionModalProps = {
  newItemId: null | number
  uploadSessionId: null | string
  showUploadAnotherItemTip?: boolean
  setNextPromotion: () => void
}

const ListPromotionModal = ({
  newItemId,
  uploadSessionId,
  showUploadAnotherItemTip,
  setNextPromotion,
}: ListPromotionModalProps) => {
  const translate = useTranslate()
  const { track } = useTracking()
  const { locale } = useIntl()

  const asset = useAsset('/assets/list-promotion')
  const isKillswitchEnabled = useFeatureSwitch('killswitch_list_another_item_promo_web')

  const [item, setItem] = useState<PartialItemBoxProps>()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [tipBody, setTipBody] = useState('')
  const [tipTitle, setTipTitle] = useState('')

  const fetchUploadAnotherItemTip = useCallback(
    async (itemId: number) => {
      if (!showUploadAnotherItemTip) {
        setNextPromotion()

        return
      }

      const response = await getUploadAnotherItemTip(itemId)

      if ('errors' in response) {
        setNextPromotion()

        return
      }

      setTipBody(response.body)
      setTipTitle(response.title)
      setIsModalOpen(true)
      setItem({
        ...response.item,
        price: formatCurrencyAmount(transformCurrencyAmountDto(response.item.price), locale),
        image: getItemThumbnail([response.item.photo], ItemThumbnailSize.Large),
        itemBox: response.item.item_box,
      })
    },
    [showUploadAnotherItemTip, locale, setNextPromotion],
  )

  useEffect(() => {
    if (!newItemId || isKillswitchEnabled) {
      setNextPromotion()

      return
    }

    fetchUploadAnotherItemTip(newItemId)
  }, [setNextPromotion, newItemId, isKillswitchEnabled, fetchUploadAnotherItemTip])

  useEffect(() => {
    if (!isModalOpen || !uploadSessionId) return

    track(
      itemUploadViewScreenEvent({
        screen: 'list_another_item',
        uploadSessionId,
      }),
    )
  }, [isModalOpen, track, uploadSessionId])

  const trackClickEvent = (target: Parameters<typeof clickEvent>[0]['target']) => {
    track(clickEvent({ target }))
  }

  const onAcceptButtonClick = () => {
    trackClickEvent('upload_more_accept')
    navigateToPage(ITEM_UPLOAD_URL)
  }

  const onSkipButtonClick = () => {
    trackClickEvent('upload_more_cancel')
    setIsModalOpen(false)
    setNextPromotion()
  }

  const renderPromotionModalBox = () => {
    return (
      <div className="u-position-relative u-flexbox u-justify-content-center u-align-items-center">
        <img
          alt=""
          width={284}
          height={387}
          src={asset('background.png')}
          className="list-promotion__custom-image-background"
        />
        <div className="list-promotion__item-wrapper">
          <Spacer size={Spacer.Size.Regular} />
          <div className="list-promotion__custom-element">
            <Card styling={Card.Styling.Elevated}>
              <Cell styling={Cell.Styling.Narrow}>
                <div className="list-promotion__custom-element--image-wrapper">
                  <Image
                    scaling={Image.Scaling.Fill}
                    src={item?.image}
                    alt={item?.itemBox?.accessibility_label}
                  />
                </div>
                <Spacer size={Spacer.Size.Regular} />
                <Text
                  width={Text.Width.Parent}
                  text={item?.itemBox?.first_line}
                  type={Text.Type.Caption}
                  as="p"
                />
                <Text
                  width={Text.Width.Parent}
                  text={item?.itemBox?.second_line}
                  type={Text.Type.Caption}
                  as="p"
                />
                <Spacer size={Spacer.Size.Small} />
                <Text text={item?.price} type={Text.Type.Subtitle} bold as="p" />
                <Spacer size={Spacer.Size.Small} />
              </Cell>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Dialog
      hasScrollableContent
      closeOnOverlay
      defaultCallback={onSkipButtonClick}
      show={!!item && isModalOpen}
      actions={[
        {
          text: translate('list_promotion.submit_cta'),
          testId: 'list-promotion-submit-cta',
          callback: onAcceptButtonClick,
          style: Dialog.ActionStyling.Filled,
        },
        {
          text: translate('list_promotion.cancel_cta'),
          testId: 'list-promotion-cancel-cta',
          callback: onSkipButtonClick,
          style: Dialog.ActionStyling.Flat,
        },
      ]}
      body={
        <>
          <Spacer size={Spacer.Size.X3Large} />
          {renderPromotionModalBox()}
          <Spacer size={Spacer.Size.Medium} />
          <Text as="h1" text={tipTitle} type={Text.Type.Heading} />
          <Spacer size={Spacer.Size.Large} />
          <Text as="span" text={tipBody} />
        </>
      }
    />
  )
}

export default ListPromotionModal
